import { useRouter } from 'next/router';
import plur from 'plur';
import type { CSSProperties } from 'react';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { shallow } from 'zustand/shallow';
import type { OptimisticDraft } from '../../types/draftable';
import DropToMove from '../DropToMove/DropToMove';
import MultiSelection from '../MultiSelection/MultiSelection';
import SelectionBox from '../SelectionBox/SelectionBox';
import { defaultZoomSequence } from '@fabric-app/ui/components/ZoomSlider/ZoomSlider';
import styles from './FdocList.module.scss';
import type {
  ApiResourcesSortField,
  OfViewMode,
  ResourceDetail,
  ResourceParent,
  ResourceDetailFolder,
  ApiResourcesFilters,
} from '@fabric-app/types/resources';
import type {
  SplitFunction,
  SplitResourceGroup,
} from '@fabric-app/business/resources/utils/splitResourceGroup/types';
import type { ResourcePreviewProps } from '@fabric-app/business/resources/components/ResourcePreview/ResourcePreview.types';
import type { ResourceUiStoreContext } from '@fabric-app/business/resources/stores/useResourcesUiStore';
import type { VirtualGridProps } from '@fabric-app/ui/components/VirtualGrid/VirtualGrid';
import { useResourceListPreferencesContext } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';
import useMobileSelectionStore from '@fabric-app/business/store/mobileSelection';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { hasMacOSKbd } from '@fabric-app/utils/detection';
import useDragMultiselect from '@/src/hooks/useDragMultiselect';
import { isTruthy } from '@fabric-app/utils/guards';
import {
  copyResourcesToClipboard,
  useResourceCopyState,
  useWindowCopyEventAddFabricResourcesToClipboardData,
} from '@fabric-app/business/resources/utils/resourceCopy/copyResourceToClipboard';
import { getMultipleResourcesLeaveOrDeleteActionDetails } from '@fabric-app/business/resource-detail/components/context/getDisabledActionsReasons';
import { useAskAssistantSelection } from '@fabric-app/business/assistant/hooks/useAskAssistantSelection';
import { useMutationDeleteLeaveResources } from '@fabric-app/business/resources/mutations/useMutationDeleteLeaveResources';
import { splitResourcesByTime } from '@fabric-app/business/resources/utils/splitResourceGroup/splitResourcesByTime';
import { decorateSplitGroupsWithTotalCount } from '@fabric-app/business/resources/utils/splitResourceGroup/decorateGroupsWithTotalCount';
import useOpenInTabs from '@/src/hooks/useOpenInTabs';
import { useMutationMoveResourcesById } from '@fabric-app/business/resources/mutations/useMutationMoveResourcesById';
import { useIsAnyModalOpen } from '@fabric-app/ui/components/Modal/useIsAnyModalOpen';
import { useMutationCopyResources } from '@fabric-app/business/resources/mutations/useMutationCopyResource';
import { useDebouncedCallback } from '@fabric-app/hooks/useDebouncedCallback';
import { useResourceClickHandler } from '@fabric-app/business/resources/components/ResourcePreview/hooks/useResourceClickHandler';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { ResourcesPreviewUiConfigProvider } from '@fabric-app/business/resources/components/ResourcePreview/resourcePreviewUiConfigContext';
import { EditTagsPerMultipleResourcesModal } from '@fabric-app/business/tags/components/EditTagsPerMultipleResourcesModal';
import { ContextMenu } from '@fabric-app/ui/components/ContextMenu/ContextMenu';
import { ResourceListContextMenuSelectedItemsContent } from './ContextMenus/ResourceListContextMenuSelectedItemsContent';
import { desktopFloatUrls } from '@/src/hooks/todesktop';
import { ResourceColorLabelColumns } from '@fabric-app/business/resources/components/ResourceList/ResourceColorLabelColumns/ResourceColorLabelColumns';
import { VirtualResourceTableDivisions } from '@fabric-app/business/resources/components/ResourceList/ResourceTable/VirtualResourceTableDivisions';
import { ResourceGridDivisions } from '@fabric-app/business/resources/components/ResourceList/ResourceGrid/ResourceGridDivisions';
import { ResourceListEmptyState } from '@fabric-app/business/resources/components/ResourceList/ResourceListEmptyState';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { Flex } from '@fabric-app/ui/components/Flex';
import type { ApiColorLabel } from '@fabric-app/types/labels';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { IconEmptyCactus } from '@fabric-app/icons/old';
import { DragAndSelectProvider } from '@fabric-app/business/lib/DragAndSelectContext';
import { useModalNewResourceContext } from '@fabric-app/business/resources/components/NewResource/ModalNewResourceContext';
import { resourceMoveModal } from '@fabric-app/business/resources/components/ModalMoveResources/moveResourcesStore';
import { useFabricEvent } from '@fabric-app/business/hooks/useFabricEvent';
import { NewResourceType } from '@fabric-app/business/resources/components/NewResource/config';
import { ResourceMasonryDivisions } from '@fabric-app/business/resources/components/ResourceList/ResourceMasonry/ResourceMasonryDivisions';
import { VirtualSearchResultList } from '@fabric-app/business/resources/components/ResourceList/ResourceSearchResultList/VirtualSearchResultList';
import { toast } from '@fabric-app/business/store/alerts';
import { useContextShareToken } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';

const zoomTrigger = (currentValue: number, direction: 'up' | 'down') => {
  const currentIndex = defaultZoomSequence.indexOf(currentValue);

  return direction === 'up'
    ? defaultZoomSequence[currentIndex + 1] || currentValue
    : defaultZoomSequence[currentIndex - 1] || currentValue;
};

const plusKeyCodes = [187, 107];
const minusKeyCodes = [189, 109];
const zeroKeyCode = [96, 48];

const FdocList: React.FC<{
  resources: OptimisticDraft<ResourceDetail>[];
  resourcesTotalCount: number;
  isLoading?: boolean;

  onClickFolder?: (resourceFolder: ResourceDetailFolder) => void;
  onClickResource?: (resource: ResourceDetail) => void;

  onInfiniteTrigger?: () => void;

  sort?: ApiResourcesSortField;
  emptyStateTitle?: string;
  emptyStateReason?: string;
  emptyStateImage?: React.ReactNode | null;
  hideContextMenu?: boolean;

  style?: React.CSSProperties;
  canMultiSelect?: boolean;
  /**
   * whether to allow actions
   *   - when user mutliselects items or not - e.g. via mutliselect context menu
   *   - when user wants to add, drop items
   * This doesn't block the actions on the resource preview context menu - it has own guards
   */
  canModifyContent: boolean;
  multiSelectLayerId?: string;
  showMultiselectBox?: boolean;
  padding?: string;
  defaultPadding?: string;
  viewMode: OfViewMode;
  colorLabels?: ApiColorLabel[];
  spaceId?: string;

  parent?: ResourceParent | null;

  skipAnimation?: boolean;
  splitFunction?: SplitFunction;

  mainListStyle?: React.CSSProperties;

  /**
   * deprecated
   */
  setScrollPosition?: (scrollPosition: number) => void;

  zoomLevel?: number;
  setZoomLevel?: (zoomLevel: number) => void;

  emptyListMessage?: string;
  splitByTime?: boolean;

  /**
   * new card previews
   */
  resourcePreviewProps?: Omit<ResourcePreviewProps, 'resource'>;
  uiContext?: ResourceUiStoreContext;

  /**
   * pass different layout id to prevent resource animation
   */
  layoutId: string;

  virtualGridOptions?: Partial<VirtualGridProps>;

  queryParams?: ApiResourcesFilters;
}> = ({
  resources,
  resourcesTotalCount,
  isLoading,
  hideContextMenu = false,
  style,
  // Not including a default for infinite trigger or it will cause re-renders
  onInfiniteTrigger: onInfiniteTriggerProp,
  sort = 'createdAt:desc,objectId:desc',
  canMultiSelect = false,
  canModifyContent = false,
  multiSelectLayerId,
  showMultiselectBox = canMultiSelect,
  mainListStyle,
  padding: paddingProp,
  defaultPadding = '50px 50px 0px 50px',
  emptyStateTitle,
  emptyStateReason,
  emptyStateImage,
  viewMode: viewModeProp,
  colorLabels,
  spaceId,
  parent,
  splitFunction,
  skipAnimation = false,

  zoomLevel = 1,
  setZoomLevel = () => {},

  emptyListMessage,
  splitByTime = false,

  resourcePreviewProps,
  uiContext,
  layoutId,

  onClickFolder,
  onClickResource,
  virtualGridOptions,
  queryParams,
}) => {
  const shareToken = useContextShareToken();
  const viewMode = viewModeProp === 'Grid' && !colorLabels ? 'Grid' : viewModeProp;

  const resourceListContextPreferences = useResourceListPreferencesContext();
  const { setNewModalOpen } = useModalNewResourceContext();

  if (!resourceListContextPreferences) {
    throw new Error('Component not wrapped with ResourceListContextPreferencesProvider!');
  }

  const router = useRouter();

  const { mobileSelectModeIsActive, disableMobileSelectMode } = useMobileSelectionStore(
    (state) => ({
      mobileSelectModeIsActive: state.mobileSelectModeIsActive,
      disableMobileSelectMode: state.disableMobileSelectMode,
    }),
    shallow,
  );

  const multiTagEditModalOpen = useBoolState();

  // listen to CMD+Scroll or CTRL+Scroll (windows) to zoom in and out
  // also listen to CMD+Plus and CMD+Minus to zoom in and out (or CTRL+Plus and CTRL+Minus on windows)
  useEffect(() => {
    const handleScroll = (e: WheelEvent) => {
      if ((!hasMacOSKbd() && !e.ctrlKey) || (hasMacOSKbd() && !e.metaKey) || viewMode === 'Kanban')
        return;

      e.preventDefault();
      const newZoomLevel = zoomTrigger(zoomLevel, e.deltaY > 0 ? 'down' : 'up');
      setZoomLevel(newZoomLevel);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((!hasMacOSKbd() && !e.ctrlKey) || (hasMacOSKbd() && !e.metaKey)) return;

      const isPlus =
        plusKeyCodes.includes(e.keyCode) ||
        plusKeyCodes.includes(e.which) ||
        e.code === 'Equal' ||
        e.code === 'NumpadAdd' ||
        e.key === '+';
      const isMinus =
        minusKeyCodes.includes(e.keyCode) ||
        minusKeyCodes.includes(e.which) ||
        e.code === 'Minus' ||
        e.code === 'NumpadSubtract' ||
        e.key === '-';
      const isZero =
        zeroKeyCode.includes(e.keyCode) ||
        zeroKeyCode.includes(e.which) ||
        e.code === 'Numpad0' ||
        e.key === '0';

      if (!isPlus && !isMinus && !isZero) return;

      if (isZero) {
        setZoomLevel(0.9);
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const newZoomLevel = zoomTrigger(zoomLevel, isPlus ? 'up' : 'down');
      setZoomLevel(newZoomLevel);
    };

    window.addEventListener('wheel', handleScroll, { passive: false });
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [zoomLevel, setZoomLevel, viewMode]);

  const innerContainerRef = useRef<HTMLDivElement>(null);

  const [resourceListContainerRef, setResourceListContainerRef] = useState<HTMLDivElement | null>(
    null,
  );

  const {
    multiSelect,
    modifierOperation,
    selectedIds: selectedResourceIds,
    activeSelectionIds: activeSelectionFdocsIds,
    setMultiSelect,
    startPosition,
    endPosition,
    setSelectedIds: setSelectedResourceIds,
    handleClearSelection,
  } = useDragMultiselect(
    canMultiSelect,
    undefined,
    (id) => !resources.find((f) => f.id === id)?.isDraft,
    multiSelectLayerId,
    resourceListContainerRef,
  );

  useEffect(() => {
    try {
      document.body.dataset.mobileSelectModeActive = `${mobileSelectModeIsActive || selectedResourceIds.length > 0}`;
    } catch {}
  }, [mobileSelectModeIsActive, selectedResourceIds]);

  const selectedResources: ResourceDetail[] = useMemo(
    () =>
      selectedResourceIds
        .map((id) => resources.find((resource) => resource.id === id && !resource.isDraft))
        .filter(isTruthy),
    [resources, selectedResourceIds],
  );

  useWindowCopyEventAddFabricResourcesToClipboardData({
    action: 'copy',
    resources: selectedResources,
  });

  const leaveDeleteAction = useMemo(() => {
    return getMultipleResourcesLeaveOrDeleteActionDetails(selectedResources || []);
  }, [selectedResources]);

  const handleSelectResource = useCallback(
    (resource: ResourceDetail) => {
      if (selectedResourceIds.includes(resource.id)) {
        setSelectedResourceIds((prev) => prev.filter((item) => item !== resource.id));
        return;
      }

      setSelectedResourceIds((prev) => [...prev, resource.id]);
    },
    [setSelectedResourceIds, selectedResourceIds],
  );

  const [contextMenuView, setContextMenuView] = useState<'FOLDER' | 'SELECTED_ITEMS'>('FOLDER');

  /**
   * our context is based on radix
   * the radix version doesn't provide any API to intercept the context menu event
   * so we create our own here, attaching it to the first child
   */
  const onContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const isTargetSelectableItem = !!(target.closest('[data-selectable-id]') as HTMLElement);

    if (isTargetSelectableItem) {
      if (selectedResourceIds.length > 1) {
        setContextMenuView('SELECTED_ITEMS');
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
      /**
       * show context menu to add items or create subfolder
       * not showing when user cannot modify content, so we don't show the context menu at all
       */
    } else if (!canModifyContent || shareToken?.isPublishedView) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      setContextMenuView('FOLDER');
    }
  };

  /******************************************************************************************************************** */

  // This will return all currently selected fdocs and space fdocs, or
  // if multiSelect is active a realtime preview of the selection
  // including modifier operation if the user is holding down a modifier key
  // which overlaps the active selection and selected fdocs
  const sanitizedSelectedResourceIds = useMemo(() => {
    if (!multiSelect) {
      // set to get unique ids
      return Array.from(new Set([...selectedResourceIds]));
    }
    if (modifierOperation) {
      // overlap of active selection and selected fdocs

      // set to get unique ids
      const selectedIdsSet = new Set([...selectedResourceIds, ...activeSelectionFdocsIds]);

      return Array.from(selectedIdsSet);
    }

    return Array.from(new Set([...activeSelectionFdocsIds]));
  }, [multiSelect, modifierOperation, selectedResourceIds, activeSelectionFdocsIds]);

  const askAssistantSelection = useAskAssistantSelection();
  const mutationDeleteResources = useMutationDeleteLeaveResources();

  const deleteSelectionHandler = () => {
    if (leaveDeleteAction.deleteOrLeaveDisabledReason) {
      toast({
        content: leaveDeleteAction.deleteOrLeaveDisabledReason,
      });
      return;
    }

    mutationDeleteResources.confirmAndMutate(
      {
        resourceIds: leaveDeleteAction?.itemsToRemove?.map((item) => item.id),
        resourceIdsToLeave: leaveDeleteAction?.itemsToLeave?.map((item) => item.id),
      },
      {
        onSuccess: handleClearSelection,
      },
    );
  };

  const moveResources = useCallback(
    (resourceIdsToMove: string[]) => {
      if (resourceIdsToMove === undefined || resourceIdsToMove.length === 0) {
        return;
      }

      const resourcesToMove = resources.filter((resource) =>
        resourceIdsToMove.includes(resource.id),
      );

      if (resourcesToMove.length === 0) {
        return;
      }

      resourceMoveModal.open({
        resources: resourcesToMove,
      });
    },
    [resources],
  );

  const askAssistantSelectionHandler = useCallback(
    (e?: React.MouseEvent, added = false) => {
      askAssistantSelection(
        selectedResources.map((f) => f.id),
        added,
      );
    },
    [askAssistantSelection, selectedResources],
  );

  const { isMobileView } = useResponsive();

  const realZoomLevel = isMobileView ? 1 : zoomLevel;

  const splitResourceGroups: SplitResourceGroup[] = useMemo(() => {
    const getTotal = () => {
      if (splitFunction)
        return splitFunction(resources, {
          spaceId,
          colorLabels,
        });
      if (splitByTime)
        return splitResourcesByTime(
          resources,
          sort?.indexOf('modified') !== -1 ? 'modifiedAt' : 'createdAt',
        );

      return [
        {
          title: '',
          resources,
          id: 'main',
        },
      ];
    };
    return decorateSplitGroupsWithTotalCount(getTotal(), resourcesTotalCount || 0);
  }, [colorLabels, resources, spaceId, sort, splitByTime, splitFunction, resourcesTotalCount]);

  const { openInTabs } = useOpenInTabs();

  const openSelectionAsTabs = useCallback(async () => {
    openInTabs(selectedResources);
    handleClearSelection();
  }, [openInTabs, selectedResources, handleClearSelection]);

  const handleClickAddItems = () => {
    setNewModalOpen(true);
  };

  const showEmpty = !isLoading && resources.length === 0 && viewMode !== 'Kanban';

  const onMoveSuccess = () => {
    setSelectedResourceIds([]);
    setMultiSelect(false);
    disableMobileSelectMode();
  };

  useFabricEvent('fabric-resources-moved', onMoveSuccess);

  const mutationMoveResources = useMutationMoveResourcesById();

  const handleCreateSubFolder = useCallback(async () => {
    if (!parent) return;

    setNewModalOpen(true, {
      type: NewResourceType.Folder,
      parent,
    });
  }, [setNewModalOpen, parent]);

  const handleCreateSubFolderAndMove = async (resourceIds: string[]) => {
    if (!parent) return;

    setNewModalOpen(true, {
      type: NewResourceType.Folder,
      parent,
      onSuccess: (newFolder) => {
        mutationMoveResources.mutate(
          {
            resourceIds,
            newParent: {
              id: newFolder.id,
              name: newFolder.name,
            },
            action: 'list-context-create-sub-folder',
          },
          {
            onSuccess: onMoveSuccess,
          },
        );
      },
    });
  };

  const isAnyModalOpen = useIsAnyModalOpen();

  /***********************************************************************
   * pasting items
   */

  const mutationCopyResources = useMutationCopyResources();

  const copyState = useResourceCopyState((s) => s.data);

  const padding = useMemo(() => {
    if (paddingProp) {
      return paddingProp;
    }
    if (viewMode === 'List' || viewMode === 'Kanban') {
      return '0px';
    }
    if (isMobileView) return '12px';

    if (viewMode === 'Detailed') {
      return '30px 30px 0 30px';
    }

    return defaultPadding;
  }, [viewMode, isMobileView, paddingProp, defaultPadding]);

  /********************************************************************************************
   *  lazy fetching
   */

  const onInfiniteTrigger = useDebouncedCallback(onInfiniteTriggerProp, 100);

  const onResourceClick = useResourceClickHandler({
    onClickFolder: mobileSelectModeIsActive ? handleSelectResource : onClickFolder,
    onClickResource: mobileSelectModeIsActive ? handleSelectResource : onClickResource,
  });

  const [innerRect] = useDomContentRect(innerContainerRef);

  const sharedViewProps = {
    disableItemPreviewContextMenu: !isMobileView && selectedResourceIds.length > 1,
    layoutId,
    colorLabels,
    onInfiniteTrigger,
    onResourceClick,
    canModifyContent,
    containerWidth: innerRect?.width || 0,
  };

  /********************************************************************************************
   *  list config
   */

  /**
   * instead of passing selected ids to render the highlight, we here provide the styles
   * to which the css can be binded to
   */
  const selectedHighlightStyles = sanitizedSelectedResourceIds.reduce((res, itemId) => {
    return {
      ...res,
      [`--item-selected-${itemId}`]: `rgba(${cssVar['color-app-primary-rgb']}, 0.05)`,
    };
  }, {} as CSSProperties);

  const moveSelectedResourceIds = useCallback(() => {
    if (leaveDeleteAction.moveDisabledReason) {
      toast({
        content: leaveDeleteAction.moveDisabledReason,
      });
      return;
    }
    moveResources(selectedResourceIds);
  }, [moveResources, selectedResourceIds, leaveDeleteAction.moveDisabledReason]);

  const copySelectedResources = useCallback(() => {
    copyResourcesToClipboard({
      action: 'copy',
      resources: selectedResources,
    });
  }, [selectedResources]);

  return (
    <ResourcesPreviewUiConfigProvider uiContext={uiContext}>
      <EditTagsPerMultipleResourcesModal
        resourceIds={selectedResourceIds}
        modalControls={multiTagEditModalOpen.modalProps}
      />

      {startPosition && endPosition && multiSelect && (
        <MultiSelection
          startPosition={startPosition}
          endPosition={endPosition}
          modifierOperation={modifierOperation}
        />
      )}

      <DragAndSelectProvider
        selectedIds={selectedResourceIds}
        clearSelection={handleClearSelection}
      >
        <ContextMenu
          contextDisabled={hideContextMenu || isMobileView || isAnyModalOpen}
          content={
            contextMenuView === 'FOLDER' ? (
              !shareToken ? (
                <>
                  <ContextMenu.Item
                    onClick={handleClickAddItems}
                    data-testid="fdoc-list-context-menu-add-items"
                  >
                    Add items...
                  </ContextMenu.Item>

                  {parent && copyState && copyState?.resources.length > 0 && (
                    <ContextMenu.Item
                      onClick={() => {
                        mutationCopyResources.mutate({
                          parent,
                          resources: copyState.resources,
                        });
                      }}
                      data-testid="fdoc-list-context-menu-paste-items"
                    >
                      Paste {copyState.resources.length} {plur('item', copyState.resources.length)}
                    </ContextMenu.Item>
                  )}

                  {parent && (
                    <ContextMenu.Item
                      onClick={handleCreateSubFolder}
                      data-testid="fdoc-list-context-menu-create-subfolder"
                    >
                      Create subfolder
                    </ContextMenu.Item>
                  )}
                </>
              ) : null
            ) : (
              selectedResourceIds.length > 1 && (
                <ResourceListContextMenuSelectedItemsContent
                  onAskAssistantSelectionClick={askAssistantSelectionHandler}
                  onAddSelectionToSpaceClick={() => moveResources(selectedResourceIds)}
                  onClickDelete={deleteSelectionHandler}
                  onOpenSelectionAsTabsClick={openSelectionAsTabs}
                  onCreateSubfolderFromSelection={parent ? handleCreateSubFolderAndMove : undefined}
                  onMultiTagEditClick={() => multiTagEditModalOpen.handleTrue()}
                  colorLabels={colorLabels}
                  resources={selectedResources}
                  spaceId={spaceId}
                  canModifyContent={canModifyContent}
                />
              )
            )
          }
        >
          <ResourceListContainer
            ref={setResourceListContainerRef}
            style={{
              opacity:
                !isLoading || desktopFloatUrls.includes(router.pathname) || skipAnimation ? 1 : 0,
              ...style,
              ...(viewMode === 'Kanban' ? { height: '100%' } : {}),
              ...selectedHighlightStyles,
              padding,
            }}
            data-multiselect-allow
            data-multiselect-layer-id={multiSelectLayerId}
            onContextMenu={onContextMenu}
          >
            <section
              ref={innerContainerRef}
              className={styles.list}
              style={{
                overflow: isLoading ? 'hidden' : undefined,
                ...mainListStyle,
              }}
              data-testid="list-items-section"
              data-view-mode={viewMode.toLowerCase()}
            >
              {resources.length > 0 || resourcesTotalCount > 0 ? (
                <>
                  {viewMode === 'Kanban' && spaceId && queryParams && !isMobileView ? (
                    <ResourceColorLabelColumns
                      folderId={spaceId}
                      queryParams={queryParams}
                      // on mobile view we still allow preview per item
                      // on desktop, it's invoking the context menu for all items
                      resourcePreviewProps={resourcePreviewProps}
                      virtualGridOptions={virtualGridOptions}
                      {...sharedViewProps}
                    />
                  ) : viewMode === 'List' ? (
                    <VirtualResourceTableDivisions
                      splitResourceGroups={splitResourceGroups}
                      //
                      canMultiSelect={canMultiSelect}
                      resourcesTotalCount={resourcesTotalCount}
                      {...sharedViewProps}
                    />
                  ) : viewMode === 'Masonry' ? (
                    <ResourceMasonryDivisions
                      splitResourceGroups={splitResourceGroups}
                      {...sharedViewProps}
                      selectedResourceIds={sanitizedSelectedResourceIds}
                      canMultiSelect={canMultiSelect}
                      resourcesTotalCount={resourcesTotalCount}
                      zoomLevel={realZoomLevel}
                      resourcePreviewProps={resourcePreviewProps}
                      virtualGridOptions={virtualGridOptions}
                    />
                  ) : viewMode === 'Detailed' ? (
                    <VirtualSearchResultList
                      resources={resources}
                      scrollYMargin={100}
                      disableDrag={!canModifyContent}
                      {...sharedViewProps}
                      selectedResourceIds={sanitizedSelectedResourceIds}
                      canMultiSelect={canMultiSelect}
                      resourcesTotalCount={resourcesTotalCount}
                      zoomLevel={realZoomLevel}
                      resourcePreviewProps={resourcePreviewProps}
                      virtualGridOptions={virtualGridOptions}
                    />
                  ) : (
                    <ResourceGridDivisions
                      splitResourceGroups={splitResourceGroups}
                      {...sharedViewProps}
                      selectedResourceIds={sanitizedSelectedResourceIds}
                      canMultiSelect={canMultiSelect}
                      resourcesTotalCount={resourcesTotalCount}
                      zoomLevel={realZoomLevel}
                      resourcePreviewProps={resourcePreviewProps}
                      virtualGridOptions={virtualGridOptions}
                    />
                  )}
                </>
              ) : null}
              {showEmpty && (
                <ResourceListEmptyState
                  image={
                    emptyStateImage === null
                      ? null
                      : emptyStateImage ||
                        (emptyStateReason && <IconEmptyCactus height={201} width={181} />)
                  }
                  title={emptyStateTitle}
                  description={emptyStateReason || emptyListMessage}
                />
              )}

              {resources.length === 0 && isLoading && (
                <LoadingContainer>
                  <Spinner size={21} />
                </LoadingContainer>
              )}
            </section>
            {canMultiSelect && showMultiselectBox && (
              <SelectionBox
                onClickMove={moveSelectedResourceIds}
                moveDisabledReason={leaveDeleteAction.moveDisabledReason}
                onClickDelete={deleteSelectionHandler}
                deleteDisabledReason={leaveDeleteAction.deleteOrLeaveDisabledReason}
                onClickClearSelection={handleClearSelection}
                onClickCopy={copySelectedResources}
                onClickEditTags={() => multiTagEditModalOpen.handleTrue()}
                itemsCount={selectedResourceIds.length}
                deleteText={leaveDeleteAction.deleteOrLeaveLabel}
                onAskAssistantSelectionClick={askAssistantSelectionHandler}
              />
            )}
            <DropToMove onDropAction={moveResources} />
          </ResourceListContainer>
        </ContextMenu>
      </DragAndSelectProvider>
    </ResourcesPreviewUiConfigProvider>
  );
};

const ResourceListContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
`;

export default memo(FdocList);

const LoadingContainer = styled(Flex).attrs({
  alignItems: 'center',
  justifyContent: 'center',
})`
  width: 100%;
  height: 100%;
`;
